// Shared ASC form definitions — imported by SurgeryFlowboard.jsx and SurgicalPortalPage.jsx

// ---------------------------------------------------------------------------
// Peri-op form registry — all sections and forms in display order
// patientFacing: true  = shown in SurgicalPortalPage pre-op forms tab
// patientFacing: false = staff-only (flowboard only)
// ---------------------------------------------------------------------------
export const FORM_SECTIONS = [
  {
    key: 'pre_arrival',
    label: 'Pre-Arrival',
    color: '#1976d2',
    bgColor: '#e3f2fd',
    forms: [
      { key: 'resp_party',        label: 'Responsible Party Info & Release',        completedBy: 'Patient',            patientFacing: true  },
      { key: 'invoice',           label: 'Invoice',                                 completedBy: 'Staff',              patientFacing: false },
      { key: 'mh_q',              label: 'Malignant Hyperthermia Questionnaire',     completedBy: 'Patient',            patientFacing: true  },
      { key: 'anesthetic_q',      label: 'Anesthetic Questionnaire',                completedBy: 'Patient',            patientFacing: true  },
      { key: 'hp',                label: 'Pre-Op H&P',                              completedBy: 'Surgeon',            patientFacing: false },
      { key: 'preop_evals',       label: 'Pre-Op Evals (Labs / EKG / Clearances)',  completedBy: 'Staff',              patientFacing: false },
    ]
  },
  {
    key: 'pre_op',
    label: 'Pre-Op',
    color: '#ed6c02',
    bgColor: '#fff3e0',
    forms: [
      { key: 'preop_orders',       label: 'Physician Pre-Op Orders',                completedBy: 'Surgeon',            patientFacing: false },
      { key: 'anesthesia_eval',    label: 'Anesthesia Pre/Post-Op Eval',             completedBy: 'Anesthesia',         patientFacing: false },
      { key: 'preg_test',          label: 'Pregnancy Test',                          completedBy: 'Nurse',              patientFacing: false },
      { key: 'hp_interval',        label: 'Surgical H&P Interval Update',            completedBy: 'Surgeon',            patientFacing: false },
      { key: 'anesthetic_plan',    label: 'Anesthetic Plan Agreement',               completedBy: 'Anesthesia',         patientFacing: false },
      { key: 'anesthesia_consent', label: 'Anesthesia Consent',                      completedBy: 'Patient',            patientFacing: true  },
      { key: 'surgical_consent',   label: 'Surgical Consent',                        completedBy: 'Patient',            patientFacing: true  },
    ]
  },
  {
    key: 'intra_op',
    label: 'Intra-Op',
    color: '#d32f2f',
    bgColor: '#ffebee',
    forms: [
      { key: 'personnel',          label: 'Personnel',                               completedBy: 'Staff',              patientFacing: false, isPersonnel: true },
      { key: 'time_out',           label: 'Surgical Time Out Attestation',           completedBy: 'Nurse / Surgeon',    patientFacing: false },
      { key: 'nursing_intraop',    label: 'Nursing Intra-Op Record',                 completedBy: 'Nurse',              patientFacing: false },
      { key: 'anesthesia_intraop', label: 'Anesthesia Intra-Op Form',                completedBy: 'Anesthesia',         patientFacing: false },
    ]
  },
  {
    key: 'post_op',
    label: 'Post-Op / PACU',
    color: '#9c27b0',
    bgColor: '#f3e5f5',
    forms: [
      { key: 'postproc_note',      label: 'Immediate Post-Procedure Note',           completedBy: 'Surgeon',            patientFacing: false },
      { key: 'postop_orders',      label: 'Physician Post-Op Orders',                completedBy: 'Surgeon',            patientFacing: false },
      { key: 'pacu_orders',        label: 'PACU Admitting Orders',                   completedBy: 'Anesthesia / Surgeon', patientFacing: false },
      { key: 'pacu_nursing',       label: 'PACU Nursing Record',                     completedBy: 'Nurse',              patientFacing: false },
      { key: 'discharge_instr',    label: 'Discharge Instructions',                  completedBy: 'Nurse',              patientFacing: false },
    ]
  },
  {
    key: 'post_discharge',
    label: 'Post-Discharge',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    forms: [
      { key: 'postdischarge_note', label: 'Post-Discharge Follow-Up Note',           completedBy: 'Surgeon',            patientFacing: false },
      { key: 'postdischarge_call', label: 'Post-Discharge Phone Call Record',        completedBy: 'Nurse',              patientFacing: false },
    ]
  }
];

// Flat list of all patient-facing form keys (for use in SurgicalPortalPage)
export const PATIENT_FORM_KEYS = FORM_SECTIONS
  .flatMap(s => s.forms)
  .filter(f => f.patientFacing)
  .map(f => f.key);

// Form keys that use the generic tblASCFormData JSON storage
export const JSON_FORM_KEYS = ['mh_q', 'anesthetic_q', 'resp_party'];

// ---------------------------------------------------------------------------
// Malignant Hyperthermia Questionnaire — question definitions
// Answers stored as JSON booleans in tblASCFormData.form_data (form_key='mh_q')
// Notes/plans stored in tblASCFormData.notes / .plans columns
// Two signature slots: sig (Anesthesia) + sig2 (Surgeon)
// ---------------------------------------------------------------------------
export const MH_QUESTIONS = [
  { key: 'q_family_death',        text: 'Do you have a family history of unexpected death(s) following general anesthesia or exercise?',          type: 'yesno_explain' },
  { key: 'q_family_mh',           text: 'Do you have a personal or family history of Malignant Hyperthermia?',                                    type: 'yesno_explain' },
  { key: 'q_neuromuscular',       text: 'Do you have a personal or family history of a muscle or neuromuscular disorder?',                        type: 'yesno_explain' },
  { key: 'q_high_temp',           text: 'Do you have a personal or family history of a high body temperature following exercise?',                type: 'yesno_explain' },
  { key: 'q_muscle_spasms',       text: 'Do you have a personal history of muscle spasms immediately following exercise?',                        type: 'yesno_explain' },
  { key: 'q_dark_urine',          text: 'Do you have a personal history of dark or chocolate colored urine following anesthesia or serious exercise?', type: 'yesno_explain' },
  { key: 'q_unanticipated_fever', text: 'Do you have a personal history of unanticipated fever immediately following anesthesia or serious exercise?', type: 'yesno_explain' }
];

// ---------------------------------------------------------------------------
// Anesthetic Questionnaire — section & question definitions (CASA form)
// Answers stored as JSON in tblASCFormData.form_data (form_key='anesthetic_q')
// ---------------------------------------------------------------------------
export const ANESTHETIC_SECTIONS = [
  {
    key: 'anesthesia_history',
    title: 'Anesthesia History',
    questions: [
      { key: 'q1', text: 'Have you ever had any problems with an anesthetic?', type: 'yesno_explain' },
      { key: 'q2', text: 'Has anyone related to you ever had problems with an anesthetic?', type: 'yesno_explain' },
      { key: 'q3', text: 'Do you or any of your relatives have Malignant Hyperthermia or Pseudocholinesterase Deficiency?', type: 'yesno' },
      { key: 'q4', text: 'Have you ever developed confusion during a hospital admission?', type: 'yesno' }
    ]
  },
  {
    key: 'social_history',
    title: 'Social History',
    subtitle: 'Smoking, alcohol, and other recreational drugs can affect your anesthetic',
    questions: [
      { key: 'q5', text: 'Do you smoke cigarettes or have you ever smoked cigarettes?', type: 'yesno',
        subFields: [
          { key: 'q5_years', label: 'How many years have you or did you smoke for?' },
          { key: 'q5_quit',  label: 'If you used to smoke, when did you quit?' }
        ]
      },
      { key: 'q6', text: 'Do you smoke or use marijuana?', type: 'yesno',
        subFields: [{ key: 'q6_how_often', label: 'How often?' }]
      },
      { key: 'q7', text: 'Do you drink alcohol or beer?', type: 'yesno',
        subFields: [
          { key: 'q7_per_day',  label: 'How many drinks per day?' },
          { key: 'q7_per_week', label: 'Per week?' }
        ]
      },
      { key: 'q8', text: 'Do you use recreational drugs (e.g. cocaine, ecstasy, crystal meth)?', type: 'yesno' }
    ]
  },
  {
    key: 'head_neck',
    title: 'Head and Neck',
    questions: [
      { key: 'q9',  text: 'Do you have dentures, caps, bridgework, implants or loose teeth?', type: 'yesno_explain' },
      { key: 'q10', text: 'Do you have any problems opening your mouth fully?', type: 'yesno' },
      { key: 'q11', text: 'Do you have problems with your neck? (e.g. arthritis, surgery, etc.)', type: 'yesno_explain' }
    ]
  },
  {
    key: 'cardiovascular',
    title: 'Cardiovascular System (heart, blood pressure)',
    questions: [
      { key: 'q12', text: 'Can you walk two blocks or climb two flights of stairs without stopping?', type: 'yesno' },
      { key: 'q13', text: 'Do you take medication to thin your blood?', type: 'yesno' },
      { key: 'q14', text: 'Have you ever had a blood transfusion?', type: 'yesno' },
      { key: 'q15', text: 'Did you have any problems following your transfusion?', type: 'yesno_explain' },
      { key: 'q16', text: 'Do you take antibiotics prior to dental work or surgery?', type: 'yesno' },
      { key: 'q17', text: 'Do you or any of your relatives have sickle cell disease or trait?', type: 'yesno' }
    ]
  },
  {
    key: 'cardiovascular_conditions',
    title: 'Have you had or do you have:',
    parentSection: 'Cardiovascular',
    questions: [
      { key: 'q18', text: 'High Blood Pressure', type: 'yesno' },
      { key: 'q19', text: 'Heart Attack or angina', type: 'yesno' },
      { key: 'q20', text: 'Stents or cardiac bypass surgery', type: 'yesno' },
      { key: 'q21', text: 'Heart failure or heart rhythm abnormalities', type: 'yesno' },
      { key: 'q22', text: 'Do you have a pacemaker or Implantable Defibrillator (ICD)?', type: 'yesno' },
      { key: 'q23', text: 'Heart Valve Problems or Valve Replacement or "murmur"', type: 'yesno' },
      { key: 'q24', text: 'Stroke or Mini Stroke (TIA)', type: 'yesno' },
      { key: 'q25', text: 'Peripheral Vascular Disease/problems with circulation', type: 'yesno' },
      { key: 'q26', text: 'Blood Clots (Phlebitis) or pulmonary embolism', type: 'yesno' }
    ]
  },
  {
    key: 'respiratory',
    title: 'Respiratory System (lungs and breathing)',
    questions: [
      { key: 'q27', text: 'Do you ever have difficulty breathing even when resting or sitting?', type: 'yesno_explain' },
      { key: 'q28', text: 'Does shortness of breath ever wake you up at night?', type: 'yesno' },
      { key: 'q29', text: 'Have you ever used puffers or home oxygen for your breathing?', type: 'yesno' }
    ]
  },
  {
    key: 'respiratory_conditions',
    title: 'Have you had or do you have:',
    parentSection: 'Respiratory',
    questions: [
      { key: 'q30', text: 'Asthma/chronic bronchitis/emphysema/wheezing/chronic cough', type: 'yesno' },
      { key: 'q31', text: 'Recent respiratory infection, cough or common cold', type: 'yesno' },
      { key: 'q32', text: 'Sleep apnea, severe snoring or breath holding at night', type: 'yesno' }
    ]
  },
  {
    key: 'other_systems',
    title: 'Other Systems',
    subtitle: 'Have you had or do you have:',
    questions: [
      { key: 'q33', text: 'Diabetes Type 1 or 2', type: 'yesno_explain' },
      { key: 'q34', text: 'Thyroid Problems', type: 'yesno_explain' },
      { key: 'q35', text: 'Hiatus Hernia/reflux or frequent acid indigestion', type: 'yesno' },
      { key: 'q36', text: "Crohn's disease or Ulcerative Colitis", type: 'yesno' },
      { key: 'q37', text: 'Rectal Bleeding or stomach ulcers', type: 'yesno' },
      { key: 'q38', text: 'Hepatitis, HIV or Tuberculosis', type: 'yesno' },
      { key: 'q39', text: 'Cirrhosis or jaundice', type: 'yesno' },
      { key: 'q40', text: 'Kidney problems or hemo/peritoneal dialysis', type: 'yesno' },
      { key: 'q41', text: 'Epilepsy or Seizures', type: 'yesno' },
      { key: 'q42', text: 'Arthritis', type: 'yesno' },
      { key: 'q43', text: 'Neurological or muscular disease', type: 'yesno_explain' },
      { key: 'q44', text: 'Glaucoma or Eye Problems', type: 'yesno_explain' },
      { key: 'q45', text: 'Chronic pain or fibromyalgia', type: 'yesno' },
      { key: 'q46', text: 'Any cortisone injections or taken any cortisone-like medication (e.g. Prednisone) in the last year', type: 'yesno' },
      { key: 'q47', text: 'Cancer — Please specify where:', type: 'yesno_explain' },
      { key: 'q48', text: 'Anemia/low blood count', type: 'yesno' },
      { key: 'q49', text: 'Is there a chance you may be pregnant?', type: 'yesno' }
    ]
  }
];
